import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Upload, X, Headphones, Image } from 'lucide-react';
import { AudioBook } from '@brovicms/models';
import audioService from '../../services/audioService';
import ImagePreviewModal from './ImagePreviewModal';
import AudioPreviewModal from './AudioPreviewModal';
import { logger } from '../../utils/logger';
import { EAUDIO_CATEGORIES } from '../../constants/categories';

interface AudioFormData {
  draftId: string;
  title: string;
  description: string;
  author: string;
  narrator: string;
  duration: number;
  language: string;
  category: {
    id: string;
    name: string;
    slug: string;
    isActive: boolean;
    sortOrder: number;
  };
  isPublished: boolean;
  isPremium: boolean;
  coverImageFile?: File | null;
  audioFile?: File | null;
  coverImage: {
    url: string;
    fileName: string;
    fileSize: number;
    mimeType: string;
    width: number;
    height: number;
  } | null;
  file: {
    url: string;
    fileName: string;
    fileSize: number;
    mimeType: string;
  } | null;
}

interface AudioFormProps {
  audio?: AudioBook | null;
  onSubmit: (success: boolean) => void;
  onCancel: () => void;
  loading: boolean;
}

const AudioForm: React.FC<AudioFormProps> = ({ audio, onSubmit, onCancel, loading }) => {
  const [draftId, setDraftId] = useState<string | null>(null);
  const isDraftCreatedRef = useRef<boolean>(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const [audioUploading, setAudioUploading] = useState(false);
  const [uploadedAudioUrl, setUploadedAudioUrl] = useState<string>('');
  const [coverImageUrl, setCoverImageUrl] = useState<string>('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  
  // Modal states
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [showAudioPreview, setShowAudioPreview] = useState(false);
  const [previewImageFile, setPreviewImageFile] = useState<File | null>(null);
  const [previewAudioFile, setPreviewAudioFile] = useState<File | null>(null);
  const [previewImageUrl, setPreviewImageUrl] = useState<string>('');
  const [previewAudioUrl, setPreviewAudioUrl] = useState<string>('');
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<AudioFormData>({
    defaultValues: {
      title: audio?.title || '',
      description: audio?.description || '',
      author: audio?.author || '',
      narrator: audio?.narrator || '',
      duration: 0,
      language: audio?.language || 'en',
      category: audio?.category || EAUDIO_CATEGORIES[0],
      isPublished: true || audio?.isPublished || false,
      isPremium: audio?.isPremium || false,
      coverImageFile: null,
      audioFile: null,
      coverImage: null,
      file: null
    },
  });

  // Create draft on component mount
  useEffect(() => {
    const createDraft = async () => {
      console.log('AudioForm___createDraft', audio, draftId, isDraftCreatedRef.current);
      if (!draftId && !isDraftCreatedRef.current) {
        try {
          const result = await audioService.createDraft();
          console.log('AudioForm___createDraft:result', result);
          setDraftId(result.data?.docId || '');
          setValue('draftId', result.data?.docId || '');
          isDraftCreatedRef.current = true;
        } catch (error) {
          console.error('Failed to create draft:', error);
        }
      }
    };

    createDraft();
  }, []);

  const handleCoverUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPreviewImageFile(file);
      const url = URL.createObjectURL(file);
      setPreviewImageUrl(url);
      setShowImagePreview(true);
    }
  };

  const confirmImageUpload = async () => {
    if (previewImageFile && draftId) {
      setCoverUploading(true);
      try {
        // Upload to Firebase Storage
        const downloadURL = await audioService.uploadFile(draftId, previewImageFile, 'cover');
        
        // Update draft with cover URL
        await audioService.updateDraft(draftId, { coverUrl: downloadURL });
        
        // Store the URL for later use
        setCoverImageUrl(downloadURL);

        setValue('coverImage', {
          url: downloadURL,
          fileName: previewImageFile.name,
          fileSize: previewImageFile.size,
          mimeType: previewImageFile.type,
          width: 0,
          height: 0,
        });

        // Close modal and cleanup
        setShowImagePreview(false);
        setPreviewImageFile(null);
        setPreviewImageUrl('');
        URL.revokeObjectURL(previewImageUrl);

      } catch (error) {
        console.error('Failed to upload cover:', error);
      } finally {
        setCoverUploading(false);
      }
    }
  };

  const cancelImageUpload = () => {
    setShowImagePreview(false);
    setPreviewImageFile(null);
    if (previewImageUrl) {
      URL.revokeObjectURL(previewImageUrl);
      setPreviewImageUrl('');
    }
    // Reset the file input so selecting the same file re-triggers onChange
    const fileInput = document.getElementById('cover-upload') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  const handleAudioUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPreviewAudioFile(file);
      const url = URL.createObjectURL(file);
      setPreviewAudioUrl(url);
      setShowAudioPreview(true);
      // Auto-fetch duration
      const audio = document.createElement('audio');
      audio.src = url;
      audio.addEventListener('loadedmetadata', () => {
        setValue('duration', Math.round(audio.duration));
      });
    }
  };

  const confirmAudioUpload = async () => {
    if (previewAudioFile && draftId) {
      setAudioUploading(true);
      try {
        // Upload to Firebase Storage
        const downloadURL = await audioService.uploadFile(draftId, previewAudioFile, 'audio');
        
        // Update draft with audio URL
        await audioService.updateDraft(draftId, { audioFileUrl: downloadURL });
        
        // Store the URL for later use
        setUploadedAudioUrl(downloadURL);
        
        // Update local state
        setValue('file', {
          url: downloadURL,
          fileName: previewAudioFile.name,
          fileSize: previewAudioFile.size,
          mimeType: previewAudioFile.type,
        });

        // Set up audio element for preview
        if (audioRef.current) {
          audioRef.current.src = downloadURL;
        }

        // Close modal and cleanup
        setShowAudioPreview(false);
        setPreviewAudioFile(null);
        setPreviewAudioUrl('');
        URL.revokeObjectURL(previewAudioUrl);

      } catch (error) {
        console.error('Failed to upload audio:', error);
      } finally {
        setAudioUploading(false);
      }
    }
  };

  const cancelAudioUpload = () => {
    setShowAudioPreview(false);
    setPreviewAudioFile(null);
    if (previewAudioUrl) {
      URL.revokeObjectURL(previewAudioUrl);
      setPreviewAudioUrl('');
    }
    // Reset the file input so selecting the same file re-triggers onChange
    const fileInput = document.getElementById('audio-upload') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  const togglePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const removeCover = async () => {
    try {
      if (coverImageUrl) {
        await audioService.deleteFileFromStorage(coverImageUrl);
      }
    } catch (e) {
      // ignore deletion failure for UX
    } finally {
      setValue('coverImageFile', null);
      setValue('coverImage', null);
      setCoverImageUrl('');
      const fileInput = document.getElementById('cover-upload') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    }
  };

  const removeAudio = async () => {
    try {
      if (uploadedAudioUrl) {
        await audioService.deleteFileFromStorage(uploadedAudioUrl);
      }
    } catch (e) {
      // ignore deletion failure for UX
    } finally {
      setValue('audioFile', null);
      setValue('file', null);
      setUploadedAudioUrl('');
      if (audioRef.current) {
        audioRef.current.src = '';
      }
      const fileInput = document.getElementById('audio-upload') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    }
  };

  const onSubmitWithValidation = async (data: AudioFormData) => {
    logger.debug('called onSubmitWithValidation', data);
    
    if (draftId) {
      try {
        const audioData: AudioBook = {
          id: draftId,
          type: 'audio',
          category: data.category || EAUDIO_CATEGORIES[0],
          tags: [],
          isFeatured: false,
          createdAt: new Date(),
          updatedAt: new Date(),
          createdBy: 'admin',
          updatedBy: 'admin',
          metadata: {
            seo: {
              title: data.title,
              description: data.description
            },
            social: {
              ogTitle: data.title,
              ogDescription: data.description
            },
            accessibility: {
              altText: data.title,
              transcripts: false
            }
          },
          stats: {
            views: 0,
            downloads: 0,
            shares: 0,
            likes: 0,
            bookmarks: 0,
            averageRating: 0,
            totalRatings: 0,
            comments: 0,
          },
          title: data.title,
          description: data.description,
          author: data.author,
          narrator: data.narrator,
          audioQuality: 'standard' as const,
          language: data.language,
          file: {
            url: data.file?.url || '',
            fileName: data.file?.fileName || '',
            fileSize: data.file?.fileSize || 0,
            mimeType: data.file?.mimeType || '',
            duration: data.duration || 0,
            bitrate: 128,
          },
          coverImage: {
            url: data.coverImage?.url || '',
            alt: data.coverImage?.fileName || '',
            width: data.coverImage?.width || 0,
            height: data.coverImage?.height || 0,
          },
          isPublished: data.isPublished,
          isPremium: data.isPremium,
        };
        console.log('AudioForm___onSubmitWithValidation:audioData', audioData, data);
        // Publish the draft
        const result = await audioService.publishDraft(draftId, audioData);
        console.log('AudioForm___publishDraft:result', result);
        onSubmit(true);
        return result;
      } catch (error) {
        console.error('Failed to publish draft:', error);
        throw error;
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmitWithValidation)} className="space-y-6">
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
        </div>
        <div className="card-body space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
              <input type="text" className={`input ${errors.title ? 'input-error' : ''}`} {...register('title', { required: 'Title is required' })} />
              {errors.title && <p className="mt-1 text-sm text-error-600">{String(errors.title.message)}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Author *</label>
              <input type="text" className={`input ${errors.author ? 'input-error' : ''}`} {...register('author', { required: 'Author is required', maxLength: { value: 50, message: 'Author name must be at most 50 characters' } })} />
              {errors.author && <p className="mt-1 text-sm text-error-600">{String(errors.author.message)}</p>}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
              <select
                className={`input ${errors.category ? 'input-error' : ''}`}
                value={(watch("category") as any)?.id || ""}
                onChange={(e) => {
                  const selectedCategory = EAUDIO_CATEGORIES.find(cat => cat.id === e.target.value);
                  if (selectedCategory) {
                    setValue("category", {
                      id: selectedCategory.id,
                      name: selectedCategory.name,
                      slug: selectedCategory.slug,
                      isActive: selectedCategory.isActive,
                      sortOrder: selectedCategory.sortOrder,
                    });
                  }
                }}
              >
                <option value="">Select category</option>
                {EAUDIO_CATEGORIES.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
              {errors.category && <p className="mt-1 text-sm text-error-600">Category is required</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Narrator</label>
              <input type="text" className="input" {...register('narrator')} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Duration (seconds)</label>
              <input type="number" className="input" {...register('duration', { valueAsNumber: true })} readOnly />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
            <textarea rows={4} className={`input ${errors.description ? 'input-error' : ''}`} {...register('description', { required: 'Description is required' })} />
            {errors.description && <p className="mt-1 text-sm text-error-600">{String(errors.description.message)}</p>}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold text-gray-900">Files</h3>
        </div>
        <div className="card-body space-y-6">
          {/* Cover Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cover Image
            </label>
            {coverImageUrl ? (
              <div className="relative inline-block">
                <img
                  src={coverImageUrl}
                  alt="Cover preview"
                  className="w-32 h-40 object-cover rounded-apple border border-gray-200"
                />
                <button
                  type="button"
                  onClick={removeCover}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-error-500 text-white rounded-full flex items-center justify-center hover:bg-error-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-apple p-6 text-center">
                <Image className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600 mb-2">Upload cover image</p>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleCoverUpload}
                  className="hidden"
                  id="cover-upload"
                />
                <label
                  htmlFor="cover-upload"
                  className={`btn btn-secondary cursor-pointer ${coverUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {coverUploading ? 'Uploading...' : 'Choose File'}
                </label>
              </div>
            )}
          </div>

          {/* Audio File */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Audio File *
            </label>
            {uploadedAudioUrl ? (
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-apple border border-gray-200">
                <div className="flex items-center">
                  <Headphones className="w-5 h-5 text-primary-500 mr-3" />
                  <span className="text-sm font-medium text-gray-900">{watch('file')?.fileName || 'Audio uploaded'}</span>
                </div>
                <button
                  type="button"
                  onClick={removeAudio}
                  className="text-error-500 hover:text-error-700"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-apple p-6 text-center">
                <Headphones className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600 mb-2">Upload audio file</p>
                <input
                  type="file"
                  accept="audio/*"
                  onChange={handleAudioUpload}
                  className="hidden"
                  id="audio-upload"
                />
                <label
                  htmlFor="audio-upload"
                  className={`btn btn-secondary cursor-pointer ${audioUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {audioUploading ? 'Uploading...' : 'Choose Audio'}
                </label>
              </div>
            )}
            {errors.audioFile && (
              <p className="mt-1 text-sm text-error-600">Audio file is required</p>
            )}
          </div>
        </div>
      </div>

      {/* <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold text-gray-900">Settings</h3>
        </div>
        <div className="card-body">
          <div className="space-y-4">
            <div className="flex items-center">
              <input type="checkbox" id="isPublished" className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" {...register('isPublished')} />
              <label htmlFor="isPublished" className="ml-2 text-sm text-gray-700">Publish immediately</label>
            </div>
            <div className="flex items-center">
              <input type="checkbox" id="isPremium" className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" {...register('isPremium')} />
              <label htmlFor="isPremium" className="ml-2 text-sm text-gray-700">Premium content</label>
            </div>
          </div>
        </div>
      </div> */}

      <div className="flex justify-end space-x-4">
        <button
          type="button"
          onClick={async () => {
            try {
              if (draftId && !audio) {
                await audioService.deleteDraftDocOnly(draftId);
              }
            } catch (e) {
              // ignore cleanup errors
            } finally {
              onCancel();
            }
          }}
          className="btn btn-secondary"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="btn btn-primary"
        >
          {loading ? 'Saving...' : audio ? 'Update Audio' : 'Create Audio'}
        </button>
      </div>

      <ImagePreviewModal
        isOpen={showImagePreview}
        imageUrl={previewImageUrl}
        fileName={previewImageFile?.name || ''}
        onConfirm={confirmImageUpload}
        onCancel={cancelImageUpload}
        isUploading={coverUploading}
      />

      <AudioPreviewModal
        isOpen={showAudioPreview}
        audioUrl={previewAudioUrl}
        fileName={previewAudioFile?.name || ''}
        onConfirm={confirmAudioUpload}
        onCancel={cancelAudioUpload}
        isUploading={audioUploading}
      />
    </form>
  );
};

export default AudioForm;


